/**
 * Klasse MATRIX_LABOR_CLIENT_PUBLIC_FED_CLOSED + MATRIX_ROOM_FIELD_CANON (Lesen)
 * Firma-Element (P3/P4). Labor-Diktat/MatrixView/Explorer darf Firma-Origins nicht als Deep-Link nutzen.
 *
 * Polar: VERBOTEN Labor-Portal → chat.arkorbit.at / matrix.arkorbit.at.
 *        VERBOTEN Stub-IDs (`labor.element.stub`, Placeholder) als Deep-Link / iframe.
 *        ERLAUBT portal-*-labor → chat-labor / matrix-labor; Firma-Portal → Firma-Origin.
 *        fail-closed: unbekannte Hosts nicht still auf Firma; leere Room-Id = ehrlicher Block.
 *
 * Host-Check spiegelt `isLaborPortalHostname` in constants.ts (kein Import:
 * Node-Tests laden constants.ts nicht wegen import.meta.env).
 * Vite injiziert MATRIX_ELEMENT_WEB_ORIGIN nicht — Host-.env gilt Element-Web/Synapse,
 * Portal-SPA entscheidet zur Laufzeit am Hostname (wie Diktat-Karte).
 */
export const FIRMA_ELEMENT_WEB_ORIGIN = 'https://chat.arkorbit.at';
export const FIRMA_MATRIX_HOMESERVER_URL = 'https://matrix.arkorbit.at';

/** Labor public Client (Federation bleibt zu). */
export const LABOR_ELEMENT_WEB_ORIGIN = 'https://chat-labor.arkorbit.at';
export const LABOR_MATRIX_HOMESERVER_URL = 'https://matrix-labor.arkorbit.at';

/**
 * @deprecated Firma-Konstante, kein Deep-Link. Labor: `portalElementWebOrigin(hostname)`.
 * P3 (Firma): self-hosted auf `chat.arkorbit.at` — Homeserver `matrix.arkorbit.at`.
 */
export const ELEMENT_WEB_ORIGIN = FIRMA_ELEMENT_WEB_ORIGIN;

/** @deprecated Firma-Konstante, kein Deep-Link. Labor: `portalMatrixHomeserverUrl(hostname)`. */
export const MATRIX_HOMESERVER_URL = FIRMA_MATRIX_HOMESERVER_URL;

/** Matrix server_name (MXID-Domain, Firma). Labor-MXID kommt vom Homeserver, nicht hier hart setzen. */
export const MATRIX_SERVER_NAME = 'arkorbit.at';

export const ELEMENT_DOWNLOAD_URL = 'https://element.io/download';

function portalHostname(explicit?: string): string {
  if (explicit && explicit.trim()) return explicit.trim().toLowerCase();
  if (typeof window !== 'undefined' && window.location?.hostname) {
    return window.location.hostname.toLowerCase();
  }
  return '';
}

function isLaborPortalHostForMatrix(hostname: string): boolean {
  const host = hostname.trim().toLowerCase();
  return (
    host === 'lab.arkorbit.at' ||
    host.includes('portal-labor') ||
    /\.labor\.arkorbit\.at$/i.test(host) ||
    host.endsWith('-labor.arkorbit.at')
  );
}

/** Bekannte Firma-Portal-Hosts — nur hier Firma-Element, sonst fail-closed. */
function isFirmaPortalHostForMatrix(hostname: string): boolean {
  const host = hostname.trim().toLowerCase();
  return host === 'portal.arkorbit.at' || host === 'demo.arkorbit.at';
}

function isKnownElementWebHostname(hostname: string): boolean {
  const host = hostname.trim().toLowerCase();
  return host === 'chat.arkorbit.at' || host === 'chat-labor.arkorbit.at';
}

/** Placeholder / Labor-Stub — kein Deep-Link, kein iframe (Klasse MATRIX_ROOM_FIELD_CANON). */
export function isStubMatrixRoomId(raw: string | undefined | null): boolean {
  const id = String(raw ?? '').trim();
  if (!id) return false;
  return (
    /labor\.element\.stub/i.test(id) ||
    /placeholder/i.test(id) ||
    /^!labor-(allgemein|team|mandanten):/i.test(id)
  );
}

/**
 * Echte Matrix-Room-Id (`!localpart:server`). Leer und Stubs sind unehrlich.
 * Absolute Element-URLs gehören in `buildElementRoomUrl`, nicht hier.
 */
export function isHonestMatrixRoomId(raw: string | undefined | null): boolean {
  const id = String(raw ?? '').trim();
  if (!id || isStubMatrixRoomId(id)) return false;
  if (/^https?:\/\//i.test(id)) return false;
  if (!id.startsWith('!') || !id.includes(':') || /\s/.test(id)) return false;
  if (id.length < 4 || id.length > 255) return false;
  return true;
}

export function roomIdFromElementUrl(absoluteUrl: string): string {
  try {
    const hash = new URL(absoluteUrl).hash || '';
    const match = hash.match(/#\/room\/([^/?]+)/i);
    return match ? decodeURIComponent(match[1]) : '';
  } catch {
    return '';
  }
}

/** Element-Web Origin: Labor-Portal → chat-labor, Firma-Portal → chat, sonst leer. */
export function portalElementWebOrigin(hostname?: string): string {
  const host = portalHostname(hostname);
  if (isLaborPortalHostForMatrix(host)) return LABOR_ELEMENT_WEB_ORIGIN;
  if (isFirmaPortalHostForMatrix(host)) return FIRMA_ELEMENT_WEB_ORIGIN;
  return '';
}

/** Homeserver-URL für Element App / Custom-Server (kein SSO, keine Federation-URL). */
export function portalMatrixHomeserverUrl(hostname?: string): string {
  const host = portalHostname(hostname);
  if (isLaborPortalHostForMatrix(host)) return LABOR_MATRIX_HOMESERVER_URL;
  if (isFirmaPortalHostForMatrix(host)) return FIRMA_MATRIX_HOMESERVER_URL;
  return '';
}

function rewriteKnownElementAbsoluteUrl(absoluteUrl: string, origin: string): string {
  if (!origin) return '';
  try {
    const parsed = new URL(absoluteUrl);
    if (!isKnownElementWebHostname(parsed.hostname)) return '';
    const hash = parsed.hash || '';
    if (hash.startsWith('#/')) return `${origin}/${hash}`;
    return `${origin}/#/login`;
  } catch {
    return '';
  }
}

/** Invite-/Share-Link auf den Host-aware Element-Origin umschreiben (kein stiller Firma-Chat auf Labor). */
export function rewriteElementAbsoluteUrl(absoluteUrl: string, hostname?: string): string {
  const base = portalElementWebOrigin(hostname).replace(/\/$/, '');
  if (!base || !String(absoluteUrl || '').trim()) return '';
  const inner = roomIdFromElementUrl(absoluteUrl);
  if (inner && !isHonestMatrixRoomId(inner)) return '';
  return rewriteKnownElementAbsoluteUrl(absoluteUrl, base);
}

/** Element-Web Deep-Link für einen Matrix-Raum (PB-Feld matrixRoomId). */
export function buildElementRoomUrl(matrixRoomId: string, hostname?: string): string {
  const id = matrixRoomId.trim();
  if (!id) return '';
  const base = portalElementWebOrigin(hostname).replace(/\/$/, '');
  if (/^https?:\/\//i.test(id)) {
    if (!base) return '';
    const inner = roomIdFromElementUrl(id);
    if (inner && !isHonestMatrixRoomId(inner)) return '';
    if (inner) return rewriteKnownElementAbsoluteUrl(id, base);
    return '';
  }
  if (!isHonestMatrixRoomId(id) || !base) return '';
  return `${base}/#/room/${encodeURIComponent(id)}`;
}

/**
 * iframe-Ziel nach Klick in der Portal-Raumliste.
 * Polar: VERBOTEN Stub, leer, Element-Startseite `#/home`.
 *        ERLAUBT nur `#/room/<ehrliche Id>`.
 */
export function kanzleiChatRoomPermalink(matrixRoomId: string, hostname?: string): string {
  if (isStubMatrixRoomId(matrixRoomId) || !isHonestMatrixRoomId(matrixRoomId)) return '';
  const url = buildElementRoomUrl(matrixRoomId, hostname);
  if (!url.includes('#/room/') || url.includes('#/home')) return '';
  return url;
}

/** Element-Web Login (PC ohne Raum-ID oder Erstlogin). Nicht als Fallback bei leerer Room-Id. */
export function buildElementLoginUrl(hostname?: string): string {
  const base = portalElementWebOrigin(hostname).replace(/\/$/, '');
  if (!base) return '';
  return `${base}/#/login`;
}

/**
 * Echte Matrix-User-Id (`@localpart:server`). Room-Ids (`!…`) und Aliase (`#…`) sind keine Konten.
 */
export function isMatrixUserId(raw: string | undefined | null): boolean {
  const id = String(raw ?? '').trim();
  if (!id.startsWith('@') || id.startsWith('!') || id.startsWith('#')) return false;
  const colon = id.indexOf(':');
  if (colon < 2 || colon === id.length - 1 || /\s/.test(id)) return false;
  if (id.includes('!')) return false;
  return id.length <= 255;
}

/**
 * Element v1.11.92: Hash-Route beendet die Browser-Sitzung auf dem Element-Origin.
 * `MatrixChat` Aktion `logout` → `Lifecycle.logout` → `onLoggedOut` →
 * `clearStorage({ deleteEverything: true })` → `localStorage.clear()`.
 * Das löscht `mx_hs_url` und `mx_sso_hs_url`. Danach bricht `?loginToken=` ab.
 * Nicht das erste iframe-Dokument des Kanzlei-Chats.
 */
export function buildElementLogoutUrl(hostname?: string): string {
  const base = portalElementWebOrigin(hostname).replace(/\/$/, '');
  if (!base) return '';
  return `${base}/#/logout`;
}

/**
 * Erstes iframe-Dokument: Homeserver setzen, ohne Logout.
 *
 * Element v1.11.92, belegt am Tag `v1.11.92`:
 * - `attemptTokenLogin` liest nur `localStorage["mx_sso_hs_url"]`
 *   (`SSO_HOMESERVER_URL_KEY` in `BasePlatform.ts`). Fehlt der Schlüssel:
 *   "Cannot log in with token: can't determine HS URL to use" (`Lifecycle.ts`).
 * - Geschrieben wird der Schlüssel nur in `startSingleSignOn`:
 *   `localStorage.setItem("mx_sso_hs_url", client.getHomeserverUrl())`.
 * - `MatrixChat.showScreen` ruft das bei Hash `#/start_sso` auf. Die URL ist
 *   `validated_server_config.hsUrl` aus `default_server_config["m.homeserver"].base_url`
 *   (`vector/app.tsx`). `?hs_url=` schreibt den Schlüssel nicht (nur E-Mail-Validierung).
 * - Labor-`config.json` hat `base_url` `https://matrix-labor.arkorbit.at`.
 *   Dieselbe Konstante liefert `portalMatrixHomeserverUrl` auf Labor-Hosts.
 *   Ohne diese Homeserver-URL bleibt die Adresse leer (fail-closed).
 * - `mx_hs_url` schreibt erst `persistCredentials` nach erfolgreichem Login.
 *   Logout davor löscht beide Schlüssel. Deshalb kein `#/logout` in dieser Adresse.
 */
export function buildElementHomeserverUrl(hostname?: string): string {
  const base = portalElementWebOrigin(hostname).replace(/\/$/, '');
  const homeserver = portalMatrixHomeserverUrl(hostname);
  if (!base || !homeserver) return '';
  return `${base}/#/start_sso`;
}

/** Same-origin Boot auf dem Element-Origin. Einziger iframe-src des Kanzlei-Chats. */
export const ELEMENT_KANZLEI_BOOT_PATH = '/kanzlei-boot.html';

/**
 * Portal-iframe nur diese Seite. Nie `#/logout`, nie direkt `#/room/`.
 * Token nur als Query. Raum-Query nur wenn GET `seat_ids` den Sitz führt.
 * Die Seite selbst geht erst nach passender gespeicherter MXID auf `#/room/`.
 */
export function buildElementKanzleiBootUrl(input: {
  hostname?: string;
  loginToken?: string;
  matrixUserId?: string;
  roomId?: string;
  seatIsMember?: boolean;
}): string {
  const base = portalElementWebOrigin(input.hostname).replace(/\/$/, '');
  if (!base) return '';
  let url: URL;
  try {
    url = new URL(ELEMENT_KANZLEI_BOOT_PATH, `${base}/`);
  } catch {
    return '';
  }
  const token = String(input.loginToken ?? '').trim();
  const mxid = String(input.matrixUserId ?? '').trim();
  if (token) url.searchParams.set('loginToken', token);
  if (isMatrixUserId(mxid)) url.searchParams.set('mxid', mxid);
  const room = String(input.roomId ?? '').trim();
  if (input.seatIsMember === true && isHonestMatrixRoomId(room) && !isStubMatrixRoomId(room)) {
    url.searchParams.set('room', room);
  }
  const src = url.toString();
  if (src.includes('#/logout') || src.includes('#/room/') || src.includes('#/start_sso')) return '';
  return src;
}

/**
 * Login-Token nur als iframe-src auf dem Element-Origin.
 * Niemals `window.location` des Portals — die Parent-Leiste bleibt ohne Token.
 */
export function buildElementLoginTokenUrl(
  loginToken: string,
  hostname?: string,
  roomId?: string,
): string {
  const token = loginToken.trim();
  const base = portalElementWebOrigin(hostname).replace(/\/$/, '');
  if (!token || !base) return '';
  const id = String(roomId ?? '').trim();
  const hash =
    id && isHonestMatrixRoomId(id) && !isStubMatrixRoomId(id)
      ? `#/room/${encodeURIComponent(id)}`
      : '#/login';
  return `${base}/?loginToken=${encodeURIComponent(token)}${hash}`;
}

/** Verstecktes iframe auf `#/logout`. Die Parent-Adresse bleibt unverändert. */
export function endElementBrowserSession(hostname?: string): void {
  const url = buildElementLogoutUrl(hostname);
  if (!url || typeof document === 'undefined') return;
  const frame = document.createElement('iframe');
  frame.setAttribute('aria-hidden', 'true');
  frame.tabIndex = -1;
  frame.title = '';
  frame.style.cssText = 'position:absolute;width:0;height:0;border:0;visibility:hidden';
  frame.src = url;
  document.body.appendChild(frame);
}

/**
 * iframe-src nur Host-aware Element-Origin (Labor → chat-labor, Firma → chat).
 * Polar: VERBOTEN Wildcard / app.element.io / stiller Firma-Chat auf Labor.
 */
export function isAllowedElementEmbedSrc(url: string, hostname?: string): boolean {
  const expected = portalElementWebOrigin(hostname);
  if (!expected || !url.trim()) return false;
  try {
    return new URL(url).origin === expected;
  } catch {
    return false;
  }
}

export type ElementShareHint = {
  kind: 'room' | 'empty' | 'origin_closed';
  url: string;
  text: string;
};

/**
 * Explorer/Teilen: Element-Deep-Link host-aware oder ehrlicher Block.
 * Polar: VERBOTEN Stub / Login-iframe / Firma-Origin auf Labor.
 */
export function elementShareHint(matrixRoomId?: string | null, hostname?: string): ElementShareHint {
  if (!isHonestMatrixRoomId(matrixRoomId)) {
    return {
      kind: 'empty',
      url: '',
      text: 'Kein Kanzlei-Raum hinterlegt — Teilen nur an Sitze, nicht nach Element.',
    };
  }
  const url = buildElementRoomUrl(String(matrixRoomId), hostname);
  if (!url) {
    return {
      kind: 'origin_closed',
      url: '',
      text: 'Element-Link für diesen Host nicht gesetzt (kein stiller Firma-Chat).',
    };
  }
  return {
    kind: 'room',
    url,
    text: 'Kanzlei-Raum in Element.',
  };
}
